import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, CheckCircle, Clock, AlertCircle, Search, Calendar, User, FileText, Download, MessageSquare, X, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MentorAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [mentees, setMentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [markComplete, setMarkComplete] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mentee_id: '',
    due_date: '',
    course_id: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchAssignments();
    fetchMentees();
  }, []);

  const fetchAssignments = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMentees = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/goals/mentees/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMentees(data.mentees || []);
      }
    } catch (error) {
      console.error('Error fetching mentees:', error);
    }
  };

  const createAssignment = async (e) => {
    e.preventDefault();
    
    if (!formData.mentee_id) {
      toast.error('Please select a mentee');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Assignment created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create assignment');
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    }
  };

  const markAsComplete = async (assignmentId) => {
    if (!window.confirm('Mark this assignment as complete?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/assignments/${assignmentId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assignment marked as complete!');
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark as complete');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assignment deleted successfully!');
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const openFeedbackModal = (assignment) => {
    setSelectedAssignment(assignment);
    setFeedbackText(assignment.mentor_feedback || '');
    setMarkComplete(false);
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!selectedAssignment || !feedbackText.trim()) {
      toast.error('Please enter feedback');
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await fetch(`${backendUrl}/api/assignments/${selectedAssignment.id}/feedback`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feedback: feedbackText,
          mark_complete: markComplete
        })
      });

      if (response.ok) {
        toast.success(markComplete ? 'Assignment marked complete with feedback!' : 'Feedback sent successfully!');
        setShowFeedbackModal(false);
        setSelectedAssignment(null);
        setFeedbackText('');
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    if (!fileUrl) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const downloadUrl = `${backendUrl}${fileUrl}`;
      
      // Make authenticated request to get the file as blob
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      mentee_id: '',
      due_date: '',
      course_id: ''
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Pending' },
      submitted: { color: 'bg-blue-100 text-blue-700', icon: FileText, text: 'Submitted' },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Completed' },
      overdue: { color: 'bg-red-100 text-red-700', icon: AlertCircle, text: 'Overdue' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${badge.color}`}>
        <Icon className="w-3 h-3" />
        <span>{badge.text}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getMenteeName = (menteeId) => {
    const mentee = mentees.find(m => m.id === menteeId);
    return mentee ? (mentee.name || mentee.email) : 'Unknown';
  };

  const filteredAssignments = assignments.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getMenteeName(a.mentee_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: assignments.length,
    pending: assignments.filter(a => a.status === 'pending').length,
    submitted: assignments.filter(a => a.status === 'submitted').length,
    overdue: assignments.filter(a => a.status === 'overdue').length,
    completed: assignments.filter(a => a.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assignment Management</h2>
            <p className="text-gray-600 mt-1">Create and track assignments for your mentees</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Assignment</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-700">Pending</p>
            <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">Submitted</p>
            <p className="text-2xl font-bold text-blue-900">{stats.submitted}</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-sm text-red-700">Overdue</p>
            <p className="text-2xl font-bold text-red-900">{stats.overdue}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm text-green-700">Completed</p>
            <p className="text-2xl font-bold text-green-900">{stats.completed}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search assignments or mentees..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="overdue">Overdue</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {filteredAssignments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No assignments found</p>
            <p className="text-gray-400 text-sm">Create your first assignment to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAssignments.map((assignment) => (
              <div key={assignment.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{assignment.title}</h3>
                      {getStatusBadge(assignment.status)}
                    </div>
                    <p className="text-gray-600 mb-3">{assignment.description}</p>
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{getMenteeName(assignment.mentee_id)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Due: {formatDate(assignment.due_date)}</span>
                      </span>
                      {assignment.submitted_at && (
                        <span className="flex items-center space-x-1 text-blue-600">
                          <FileText className="w-4 h-4" />
                          <span>Submitted: {formatDate(assignment.submitted_at)}</span>
                        </span>
                      )}
                    </div>
                    
                    {/* Submission Notes */}
                    {assignment.submission_notes && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-900 font-medium mb-1">Submission Notes:</p>
                        <p className="text-sm text-blue-700">{assignment.submission_notes}</p>
                      </div>
                    )}
                    
                    {/* Uploaded File */}
                    {assignment.submission_file_url && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-green-800 font-medium">Submitted File</span>
                          </div>
                          <button
                            onClick={() => downloadFile(assignment.submission_file_url, assignment.submission_file_name || 'submission')}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Existing Feedback */}
                    {assignment.mentor_feedback && (
                      <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm text-purple-900 font-medium mb-1">Your Feedback:</p>
                        <p className="text-sm text-purple-700">{assignment.mentor_feedback}</p>
                        {assignment.feedback_at && (
                          <p className="text-xs text-purple-500 mt-2">Sent: {formatDate(assignment.feedback_at)}</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {/* Provide Feedback Button (for submitted assignments) */}
                    {(assignment.status === 'submitted' || assignment.status === 'completed') && (
                      <button
                        onClick={() => openFeedbackModal(assignment)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Provide feedback"
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>
                    )}
                    
                    {/* Mark Complete Button */}
                    {assignment.status === 'submitted' && (
                      <button
                        onClick={() => markAsComplete(assignment.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Mark as complete"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => deleteAssignment(assignment.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete assignment"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Assignment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Assignment</h2>

              <form onSubmit={createAssignment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignment Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                    placeholder="e.g., Complete Chapter 1 Reading"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                    placeholder="Provide detailed instructions for the assignment..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to Mentee *
                  </label>
                  <select
                    value={formData.mentee_id}
                    onChange={(e) => setFormData({...formData, mentee_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a mentee...</option>
                    {mentees.map((mentee) => (
                      <option key={mentee.id} value={mentee.id}>
                        {mentee.name || mentee.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                  >
                    Create Assignment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Provide Feedback</h2>
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setSelectedAssignment(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Assignment Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{selectedAssignment.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{selectedAssignment.description}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{getMenteeName(selectedAssignment.mentee_id)}</span>
                  </span>
                  {selectedAssignment.submitted_at && (
                    <span className="text-blue-600">Submitted: {formatDate(selectedAssignment.submitted_at)}</span>
                  )}
                </div>
              </div>

              {/* Submission Details */}
              {selectedAssignment.submission_notes && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-1">Mentee's Notes:</p>
                  <p className="text-sm text-blue-700">{selectedAssignment.submission_notes}</p>
                </div>
              )}

              {/* Download File */}
              {selectedAssignment.submission_file_url && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-800 font-medium">Submitted File</span>
                    </div>
                    <button
                      onClick={() => downloadFile(selectedAssignment.submission_file_url, selectedAssignment.submission_file_name || 'submission')}
                      className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download & Review</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Feedback Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Feedback *
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Provide detailed feedback on the submission..."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="markComplete"
                    checked={markComplete}
                    onChange={(e) => setMarkComplete(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="markComplete" className="text-sm text-gray-700">
                    Mark assignment as complete
                  </label>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={submitFeedback}
                    disabled={submittingFeedback || !feedbackText.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {submittingFeedback ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-4 h-4" />
                        <span>Send Feedback</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowFeedbackModal(false);
                      setSelectedAssignment(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorAssignments;
